/**
 * Admin-portal host detection.
 *
 * The global-admin portal is served at a dedicated subdomain
 * (admin.karexia.com) by its own Cloudflare Worker (pubsocialnet-admin-prod),
 * but ships from the SAME SPA build as the tenant app. This helper lets the
 * bootstrap decide, at runtime, whether the current host is the admin portal so
 * it can route `admin.*` visitors straight into the `/admin` portal shell.
 *
 * Host-only detection (no path coupling): any hostname beginning with `admin.`
 * — admin.karexia.com in prod, admin.dev.karexia.com / admin.qa.karexia.com in
 * lower envs, admin.localhost for local dev — is the admin portal. The `/admin`
 * When the tenant app's config.json sets `adminPortalUrl` (prod), `/admin/*`
 * on the app host redirects to the admin host, so the console runs only on its
 * own origin (separate session storage, room for host-level protection); the
 * admin host sends tenant pages back to `appUrl`. Redirects are never the access
 * control: the `platform_superadmin` realm-role guard on AdminPortalLayout and
 * the backend role checks are authoritative.
 */

import { getConfig, type AppConfig } from '@/config';

/** True when the current (or given) hostname is an admin-portal host. */
export function isAdminHost(hostname: string = typeof location !== 'undefined' ? location.hostname : ''): boolean {
  return hostname.toLowerCase().startsWith('admin.');
}

/** The portal home path the admin subdomain should land on. */
export const ADMIN_PORTAL_HOME = '/admin';

type HostConfig = Pick<AppConfig, 'adminPortalUrl' | 'appUrl'>;

function currentConfig(): HostConfig {
  try {
    return getConfig();
  } catch {
    return {};
  }
}

const joinOrigin = (origin: string, path: string) => origin.replace(/\/+$/, '') + path;

/**
 * Absolute URL of `path` on the dedicated admin host, when the console must be
 * served from there: the tenant app has `adminPortalUrl` configured and we are
 * not already on an admin host. Null means "stay here".
 */
export function adminPortalUrlFor(
  path: string,
  config: HostConfig = currentConfig(),
  hostname?: string,
): string | null {
  if (!config.adminPortalUrl || isAdminHost(hostname)) return null;
  return joinOrigin(config.adminPortalUrl, path);
}

/**
 * Absolute URL of a tenant-app `path` on the app host, when the admin host
 * should not serve tenant pages: we are on an admin host with `appUrl`
 * configured. The admin home (`/`) stays, since it redirects to the console.
 */
export function appUrlFor(path: string, config: HostConfig = currentConfig(), hostname?: string): string | null {
  if (!config.appUrl || !isAdminHost(hostname) || path === '/' || path.startsWith('/?')) return null;
  return joinOrigin(config.appUrl, path);
}
